import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { BarraDeLista, FiltrosAtivos, type FiltroAtivo } from '../../BarraDeLista';
import { Seletor } from '../../Filtros';
import { diaDaSemanaEDia, hojeIso, numero, plural, pluralSemNumero } from '../../formato';
import { CartaoDePendencia } from '../../painel/CartaoDePendencia';
import { IndicadoresDoPeriodo, PERIODOS } from '../../painel/IndicadoresDoPeriodo';
import { PainelDoEspaco } from '../../painel/PainelDoEspaco';
import type { Indicador } from '../../painel/tipos';
import { usePainelVivo } from '../../painel/usePainelVivo';
import { useSessao } from '../../sessao';
import { degrauDe, ESTAGIO_DA_OPORTUNIDADE } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Aviso, Carregando, ErroDaApi, Vazio } from '../../ui';
import {
  LINHA, MOTIVO_DA_PERDA, PAPEL_DA_IA, pendenciaDoRadar, reais, reaisRedondos, regraDoRadar, textoDe,
  type LimitesDoRadar, type LinhaDoRadar, type Pessoa,
} from './crm';
import './painelComercial.css';

/**
 * O painel do Comercial — onda 552 (Plano 2 · Comercial, Etapa 1, Tarefa 28;
 * spec do Comercial §8.4, §1.1, §3.6). `/comercial/painel`.
 *
 * O molde dos painéis de área (`web/src/painel/`, RN-256, P-UI-37):
 *
 *  · «Hoje»: os itens do Radar (`GET /comercial/radar`) como cartões da
 *    pilha (`CartaoDePendencia`), do mesmo jeito que no Início do Hub — a
 *    palavra da escada do Radar com a cor da pilha, o «i» com a regra dos
 *    níveis, «Tudo | Comigo» em cima. Com a diretoria filtrando um
 *    responsável, é o Radar DELE (T28-04);
 *  · «Andamento»: os números do período (`IndicadoresDoPeriodo`, 7 · 30 · 90
 *    dias, comparados com o período anterior) e, à parte, os de AGORA (o
 *    valor em aberto e o gasto da IA do mês), que o período não muda (T28-07);
 *  · embaixo, o detalhe do período (por canal, as conversas iniciadas pela
 *    equipe por dia — 0542, Parte B §8 —, por linha, as perdas) e o de
 *    agora (o funil e o gasto por papel).
 *
 * A regra é do banco (`fn_painel_comercial`, 0531): a primeira resposta em
 * minutos CORRIDOS (a meta é 2 minutos, 24 h por dia), a conversão sem a
 * descartada, o valor em aberto por oportunidade. Quem vê o quê também: sem
 * a diretoria, a pessoa vê os próprios números (o filtro de responsável nem
 * aparece); sem o Comercial, não vem valor nenhum — e a tela não mostra o
 * número, em vez de mostrar zero.
 *
 * O resumo de quatro números do LabManager (`ResumoDoPainel`) não entra: os
 * grupos dele são os do laboratório (travado, esperando, passou da data,
 * vence). A pilha inteira (`PilhaDePendencias`) também não: ela só mostra
 * cartão com número, e o do Radar é de um registro só (como no Hub).
 *
 * O período, a linha, o responsável, o «Comigo» e a aba ficam no endereço.
 */

interface PorCanal { canal: string; nome: string; conversas: number; respondidas: number; semResposta: number;
  medianaMin: number | null; p90Min: number | null }
interface PorLinha { linha: string | null; ganhas: number; perdidas: number; taxa: number }
interface DadosDoPainel {
  periodo: { de: string; ate: string };
  diretoria: boolean;
  veComercial: boolean;
  responsavel: Pessoa | null;
  primeiraResposta: { conversas: number; respondidas: number; semResposta: number; medianaMin: number | null;
    p90Min: number | null; metaMin: number };
  primeiraRespostaPorCanal: PorCanal[];
  sugestoesSemCorrecao: { resolvidas: number; semCorrecao: number; taxa: number | null; meta: number };
  /** A conversa ativa (0542, Parte B §8): as que a equipe abriu para o cliente, por dia de São Paulo. */
  conversasIniciadas?: { total: number; porDia: { dia: string; conversas: number }[] };
  conversao?: { ganhas: number; perdidas: number; taxa: number | null };
  conversaoPorLinha?: PorLinha[];
  motivosDePerda?: { motivo: string; quantas: number }[];
  funil?: { estagio: string; quantas: number }[];
  valorEmPropostaAberta?: { valor: number; propostas: number };
  pedidoAPropostaMedianaHoras?: number | null;
  pedidoAPropostaPropostas?: number;
  gastoIaPorPapel?: { mes: string; total: number; teto: number; porPapel: { papel: string; gasto: number }[] };
}

const PERIODO_PADRAO = 30;
const VAZIO = 'Nenhuma conversa no período';

/** O dia `iso` menos `n` dias (o meio-dia em UTC não tropeça em fuso nenhum). */
const menosDias = (iso: string, n: number) =>
  new Date(Date.parse(`${iso}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);

const minutos = (v: number) => numero(v, 1, true);
const porcento = (v: number) => numero(v * 100, 0);

/**
 * A comparação com o período anterior, em palavra (RN-256, 5): «2 min a
 * menos», «5 pontos a mais», «igual». O tom só reforça: na primeira
 * resposta e no pedido à proposta, menos é bom; na conversão, mais.
 */
function comparar(atual: number | null | undefined, antes: number | null | undefined,
  { unidade, menorEhMelhor, casas = 1, escala = 1 }: { unidade: [string, string]; menorEhMelhor: boolean;
    casas?: number; escala?: number }): Indicador['comparacao'] {
  /* Sem o número de um dos dois períodos não há comparação: a linha some, em vez de dizer o óbvio. */
  if (atual === null || atual === undefined || antes === null || antes === undefined) return undefined;
  const d = Math.round((atual - antes) * escala * 10 ** casas) / 10 ** casas;
  if (d === 0) return { texto: 'igual ao período anterior', tom: 'neutro' };
  const melhor = menorEhMelhor ? d < 0 : d > 0;
  return {
    texto: `${numero(Math.abs(d), casas, true)} ${pluralSemNumero(Math.abs(d), ...unidade)} a ${d < 0 ? 'menos' : 'mais'}`,
    tom: melhor ? 'bom' : 'ruim',
  };
}

/** Os números do PERÍODO: mudam com o 7 · 30 · 90 e se comparam com o anterior. */
function indicadoresDe(d: DadosDoPainel, antes: DadosDoPainel | null): Indicador[] {
  const pr = d.primeiraResposta;
  const lista: Indicador[] = [{
    chave: 'primeira_resposta',
    rotulo: 'Primeira resposta (mediana)',
    valor: pr.medianaMin === null ? '—' : minutos(pr.medianaMin),
    unidade: pr.medianaMin === null ? undefined : 'min',
    detalhe: pr.conversas === 0 ? VAZIO
      : [pr.p90Min !== null ? `9 de cada 10 em até ${minutos(pr.p90Min)} min` : null,
        `meta: ${pr.metaMin} min`,
        pr.semResposta === 0 ? 'todas respondidas' : plural(pr.semResposta, 'sem resposta', 'sem resposta')]
        .filter(Boolean).join(' · '),
    comparacao: pr.conversas === 0 ? undefined
      : comparar(pr.medianaMin, antes?.primeiraResposta.medianaMin, { unidade: ['min', 'min'], menorEhMelhor: true }),
  }];
  if (d.veComercial) {
    const h = d.pedidoAPropostaMedianaHoras ?? null;
    lista.push({
      chave: 'pedido_a_proposta',
      rotulo: 'Pedido à proposta (mediana)',
      valor: h === null ? '—' : numero(h, 1, true),
      unidade: h === null ? undefined : 'h',
      detalhe: h === null ? 'Nenhuma proposta enviada no período'
        : plural(d.pedidoAPropostaPropostas ?? 0, 'proposta enviada', 'propostas enviadas'),
      comparacao: h === null ? undefined
        : comparar(h, antes?.pedidoAPropostaMedianaHoras, { unidade: ['hora', 'horas'], menorEhMelhor: true }),
    });
    const c = d.conversao!;
    lista.push({
      chave: 'conversao',
      rotulo: 'Conversão',
      valor: c.taxa === null ? '—' : porcento(c.taxa),
      unidade: c.taxa === null ? undefined : '%',
      detalhe: c.taxa === null ? 'Nenhuma ganha ou perdida no período'
        : `${c.ganhas === 0 ? 'Nenhuma ganha' : plural(c.ganhas, 'ganha', 'ganhas')} de ${plural(c.ganhas + c.perdidas, 'fechada', 'fechadas')}`,
      comparacao: c.taxa === null ? undefined
        : comparar(c.taxa, antes?.conversao?.taxa, { unidade: ['ponto', 'pontos'], menorEhMelhor: false, casas: 0, escala: 100 }),
    });
  }
  const s = d.sugestoesSemCorrecao;
  lista.push({
    chave: 'sugestoes',
    rotulo: 'Sugestões da IA sem correção',
    valor: s.taxa === null ? '—' : porcento(s.taxa),
    unidade: s.taxa === null ? undefined : '%',
    detalhe: s.taxa === null ? `Nenhuma sugestão resolvida no período · meta: ${porcento(s.meta)} %`
      : `${s.semCorrecao === 0 ? 'nenhuma' : s.semCorrecao} de ${plural(s.resolvidas, 'resolvida', 'resolvidas')} · meta: ${porcento(s.meta)} %`,
  });
  return lista;
}

/** Os números de AGORA (T28-07): o período não os muda, e não há «anterior» com que comparar. */
function numerosDeAgora(d: DadosDoPainel): Indicador[] {
  const lista: Indicador[] = [];
  if (d.valorEmPropostaAberta) {
    const v = d.valorEmPropostaAberta;
    lista.push(v.propostas === 0 ? {
      chave: 'valor_aberto', rotulo: 'Em proposta aberta', valor: '—',
      detalhe: 'Nenhuma proposta enviada ou em negociação agora',
    } : {
      chave: 'valor_aberto', rotulo: 'Em proposta aberta', valor: reaisRedondos(v.valor),
      detalhe: plural(v.propostas, 'proposta enviada ou em negociação', 'propostas enviadas ou em negociação'),
    });
  }
  if (d.gastoIaPorPapel) {
    const g = d.gastoIaPorPapel;
    /* As mesmas casas no gasto e no teto (T28-08, P-FMT-08). */
    lista.push({
      chave: 'gasto_ia',
      rotulo: 'Gasto da IA neste mês',
      valor: reais(g.total),
      detalhe: g.teto > 0 ? `${porcento(g.total / g.teto)} % do teto de ${reais(g.teto)}` : 'Nenhum teto definido',
    });
  }
  return lista;
}

/** Os números de agora, nas mesmas peças do Andamento (`pn-kpi`), com o título que diz que não seguem o período. */
function Agora({ indicadores }: { indicadores: Indicador[] }) {
  if (indicadores.length === 0) return null;
  return (
    <section className="pcm-agora" aria-labelledby="pcm-agora-titulo">
      <h3 id="pcm-agora-titulo" className="pcm-agora-titulo">Agora e neste mês</h3>
      <p className="pn-periodo-sub">não mudam com o período</p>
      <div className="pn-kpis pcm-kpis-agora">
        {indicadores.map((i) => (
          <article key={i.chave} className="pn-kpi" data-chave={i.chave}>
            <h4 className="pn-kpi-rot">{i.rotulo}</h4>
            <p className="pn-kpi-valor"><span className="pn-kpi-num">{i.valor}</span></p>
            {i.detalhe && <p className="pn-kpi-det">{i.detalhe}</p>}
          </article>
        ))}
      </div>
    </section>
  );
}

/** Um bloco do detalhe: o título e as linhas «rótulo · número». */
function Bloco({ chave, titulo, children }: { chave: string; titulo: string; children: ReactNode }) {
  return (
    <section className="cartao pcm-bloco" data-bloco={chave} aria-labelledby={`pcm-${chave}`}>
      <h3 id={`pcm-${chave}`} className="pcm-bloco-titulo">{titulo}</h3>
      {children}
    </section>
  );
}

function Linhas({ itens }: { itens: { chave: string; rotulo: string; valor: string; apoio?: string }[] }) {
  return (
    <ul className="pcm-linhas">
      {itens.map((i) => (
        <li key={i.chave} className="pcm-linha" data-chave={i.chave}>
          <span className="pcm-rotulo">{i.rotulo}{i.apoio && <span className="pcm-apoio">{i.apoio}</span>}</span>
          <span className="pcm-valor">{i.valor}</span>
        </li>
      ))}
    </ul>
  );
}

function Detalhes({ d }: { d: DadosDoPainel }) {
  return (
    <>
      <section className="pn-abaixo pcm-detalhes" aria-labelledby="pcm-detalhes-titulo">
        <h2 id="pcm-detalhes-titulo" className="pn-abaixo-titulo">O detalhe do período</h2>
        <div className="pcm-grade">
          <Bloco chave="canais" titulo="Primeira resposta por canal">
            {d.primeiraRespostaPorCanal.length === 0 ? <Vazio>{VAZIO}.</Vazio> : (
              <Linhas itens={d.primeiraRespostaPorCanal.map((c) => ({
                chave: c.canal,
                rotulo: c.nome,
                apoio: `${plural(c.conversas, 'conversa', 'conversas')} · ${c.semResposta === 0 ? 'todas respondidas'
                  : plural(c.semResposta, 'sem resposta', 'sem resposta')}`,
                valor: c.medianaMin === null ? 'sem resposta ainda' : `${minutos(c.medianaMin)} min`,
              }))} />
            )}
          </Bloco>
          {d.conversasIniciadas && (
            <Bloco chave="iniciadas" titulo="Conversas iniciadas pela equipe">
              {d.conversasIniciadas.total === 0 ? <Vazio>Nenhuma conversa iniciada pela equipe no período.</Vazio> : (
                <>
                  <p className="pcm-total">
                    {plural(d.conversasIniciadas.total, 'conversa iniciada', 'conversas iniciadas')} no período
                  </p>
                  <Linhas itens={d.conversasIniciadas.porDia.map((x) => ({
                    chave: x.dia, rotulo: diaDaSemanaEDia(x.dia), valor: plural(x.conversas, 'conversa', 'conversas'),
                  }))} />
                </>
              )}
            </Bloco>
          )}
          {d.conversaoPorLinha && (
            <Bloco chave="linhas" titulo="Conversão por linha">
              {d.conversaoPorLinha.length === 0 ? <Vazio>Nenhuma ganha ou perdida no período.</Vazio> : (
                <Linhas itens={d.conversaoPorLinha.map((l) => ({
                  chave: l.linha ?? 'sem_linha',
                  rotulo: l.linha ? textoDe(LINHA, l.linha) : 'Linha a identificar',
                  apoio: [l.ganhas === 0 ? 'nenhuma ganha' : plural(l.ganhas, 'ganha', 'ganhas'),
                    l.perdidas === 0 ? 'nenhuma perdida' : plural(l.perdidas, 'perdida', 'perdidas')].join(' · '),
                  valor: `${porcento(l.taxa)} %`,
                }))} />
              )}
            </Bloco>
          )}
          {d.motivosDePerda && (
            <Bloco chave="perdas" titulo="Motivos de perda">
              {d.motivosDePerda.length === 0 ? <Vazio>Nenhuma perdida no período.</Vazio> : (
                <Linhas itens={d.motivosDePerda.map((m) => ({
                  chave: m.motivo, rotulo: textoDe(MOTIVO_DA_PERDA, m.motivo), valor: plural(m.quantas, 'perdida', 'perdidas'),
                }))} />
              )}
            </Bloco>
          )}
        </div>
      </section>
      {(d.funil || d.gastoIaPorPapel) && (
        <section className="pn-abaixo pcm-detalhes" aria-labelledby="pcm-agora-detalhe-titulo">
          {/* D3 (Humberto 01/11): o gasto da IA é só da diretoria — sem ele, o título fala só do funil. */}
          <h2 id="pcm-agora-detalhe-titulo" className="pn-abaixo-titulo">
            {d.gastoIaPorPapel ? 'O funil e o gasto por papel' : 'O funil'}
          </h2>
          <div className="pcm-grade">
            {d.funil && (
              <Bloco chave="funil" titulo="O funil agora">
                <Linhas itens={d.funil.map((f) => ({
                  chave: f.estagio,
                  rotulo: degrauDe(ESTAGIO_DA_OPORTUNIDADE, f.estagio).texto,
                  valor: f.quantas === 0 ? 'nenhum cartão' : plural(f.quantas, 'cartão', 'cartões'),
                }))} />
              </Bloco>
            )}
            {d.gastoIaPorPapel && (
              <Bloco chave="ia" titulo="Gasto da IA neste mês, por papel">
                <Linhas itens={d.gastoIaPorPapel.porPapel.map((p) => ({
                  chave: p.papel, rotulo: textoDe(PAPEL_DA_IA, p.papel), valor: reais(p.gasto),
                }))} />
              </Bloco>
            )}
          </div>
        </section>
      )}
    </>
  );
}

const NADA_GRAVE = 'Nada crítico e nada em risco agora.';
const NADA_GRAVE_COMPLEMENTO = 'O Radar mostra o que está parado: a conversa sem a nossa resposta, a proposta sem '
  + 'próxima ação e a tarefa vencida.';
const TUDO = 'Tudo';
const COMIGO = 'Comigo';

/**
 * O «Hoje»: o Radar em cartões da pilha (T28-03). «Comigo» é o «Meus» do
 * Radar (o que é meu: eu ajo, a conversa é minha, a proposta é minha ou
 * indiquei), e nasce ligado, no endereço como lá (`meus=0` desliga). Com a
 * diretoria filtrando um responsável, o Radar é o dele, e o «Tudo | Comigo»
 * dá lugar à frase que diz de quem é (T28-04).
 */
function HojeDoComercial({ responsavel, nomeDoResponsavel }: { responsavel: string | null; nomeDoResponsavel: string | null }) {
  const [sp, setSp] = useSearchParams();
  const comigo = sp.get('meus') !== '0';
  const buscar = useCallback(() => {
    const q = new URLSearchParams({ meus: comigo ? '1' : '0' });
    if (responsavel) q.set('responsavel', responsavel);
    return api<{ linhas: LinhaDoRadar[]; limites: LimitesDoRadar }>(`/comercial/radar?${q}`);
  }, [comigo, responsavel]);
  /* O nível anda com o relógio: de minuto em minuto, e ao vivo pela caixa e pelo funil. */
  const { dados, erro, recarregar } = usePainelVivo(buscar, 60_000);
  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(['comercial.caixa', 'comercial.funil'], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; recarregar(); }, 300);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  const mudar = (v: boolean) => {
    const b = new URLSearchParams(sp);
    if (v) b.delete('meus'); else b.set('meus', '0');
    setSp(b, { replace: true });
  };

  const explicacao = dados ? regraDoRadar(dados.limites) : undefined;
  const pendencias = useMemo(
    /* D7: uma linha por oportunidade. */
    () => (dados ? dados.linhas.map((i) => pendenciaDoRadar(i, explicacao)) : []), [dados, explicacao]);
  const grave = pendencias.some((p) => p.gravidade === 'trava' || p.gravidade === 'atencao');

  return (
    <div className="pcm-hoje" data-estado={dados ? 'pronto' : 'carregando'}>
      <AtualizacaoPausada estado={tempoReal} />
      {responsavel ? (
        <p className="pcm-de-quem">O que é de {nomeDoResponsavel ?? 'quem você escolheu'} no Radar agora.</p>
      ) : (
        <div className="pn-filtros">
          <div className="pn-de-quem" role="group" aria-label="De quem">
            <button type="button" className="pn-quem" aria-pressed={!comigo} onClick={() => mudar(false)}>{TUDO}</button>
            <button type="button" className="pn-quem" aria-pressed={comigo} onClick={() => mudar(true)}>{COMIGO}</button>
          </div>
        </div>
      )}
      {erro && !dados ? <ErroDaApi erro={erro} />
        : !dados ? <Carregando texto="Carregando o Radar" />
          : (
            <>
              {!grave && <Aviso tipo="ok" titulo={NADA_GRAVE}>{NADA_GRAVE_COMPLEMENTO}</Aviso>}
              {pendencias.length > 0 && (
                <div className="pn-cartoes">
                  {dados.linhas.map((i, n) => (
                    <div key={pendencias[n].chave} className="pcm-item" data-id={i.referenciaId} data-tipo={i.tipo}
                      data-nivel={i.nivel}>
                      <CartaoDePendencia p={pendencias[n]} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
      <Link className="botao secundario pcm-radar" to="/comercial/radar">Abrir o Radar</Link>
    </div>
  );
}

export function PainelComercial() {
  const { pode } = useSessao();
  const diretoria = pode('comercial.diretoria');
  const [sp, setSp] = useSearchParams();
  const diasPedido = Number(sp.get('dias'));
  const dias = (PERIODOS as readonly number[]).includes(diasPedido) ? diasPedido : PERIODO_PADRAO;
  const linha = sp.get('linha');
  const responsavel = diretoria ? sp.get('responsavel') : null;
  const [responsaveis, setResponsaveis] = useState<Pessoa[]>([]);

  const mudar = (valores: Record<string, string | null>) => {
    const b = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(valores)) { if (v) b.set(k, v); else b.delete(k); }
    setSp(b, { replace: true });
  };

  /* O período e o anterior, do mesmo tamanho, para a comparação. */
  const buscar = useCallback(async () => {
    const ate = hojeIso();
    const de = menosDias(ate, dias - 1);
    const pedir = (a: string, b: string) => {
      const q = new URLSearchParams({ de: a, ate: b });
      if (linha) q.set('linha', linha);
      if (responsavel) q.set('responsavel', responsavel);
      return api<DadosDoPainel>(`/comercial/painel?${q}`);
    };
    const [atual, anterior] = await Promise.all([
      pedir(de, ate), pedir(menosDias(de, dias), menosDias(de, 1)).catch(() => null)]);
    return { atual, anterior };
  }, [dias, linha, responsavel]);
  const { dados, erro, atualizadoEm } = usePainelVivo(buscar);

  useEffect(() => {
    if (!diretoria) return;
    api<Pessoa[]>('/comercial/oportunidades/responsaveis').then(setResponsaveis).catch(() => setResponsaveis([]));
  }, [diretoria]);

  const indicadores = useMemo(() => (dados ? indicadoresDe(dados.atual, dados.anterior) : []), [dados]);

  const filtros: FiltroAtivo[] = [
    ...(responsavel ? [{ chave: 'responsavel',
      texto: `Responsável: ${responsaveis.find((p) => p.id === responsavel)?.nome ?? dados?.atual.responsavel?.nome ?? '…'}`,
      tirar: () => mudar({ responsavel: null }) }] : []),
    ...(linha ? [{ chave: 'linha', texto: `Linha: ${textoDe(LINHA, linha)}`, tirar: () => mudar({ linha: null }) }] : []),
  ];
  const limpar = () => mudar({ responsavel: null, linha: null });

  const andamento = (
    <>
      <BarraDeLista rotulo="Filtros do painel" ativos={filtros.length} aoLimpar={limpar}>
        {diretoria && (
          <Seletor naBarra rotulo="Responsável" valor={responsavel} todos="Todos"
            opcoes={responsaveis.map((p) => ({ valor: p.id, texto: p.nome }))} aoMudar={(v) => mudar({ responsavel: v })} />
        )}
        <Seletor naBarra rotulo="Linha" valor={linha} todos="Todas as linhas"
          opcoes={Object.entries(LINHA).map(([valor, t]) => ({ valor, texto: t }))} aoMudar={(v) => mudar({ linha: v })} />
      </BarraDeLista>
      <FiltrosAtivos itens={filtros} aoLimpar={limpar}
        contagem={diretoria ? (responsavel ? undefined : 'Os números de todo o Comercial') : 'Os seus números'} />
      <IndicadoresDoPeriodo indicadores={indicadores} dias={dias} aoDias={(n) => mudar({ dias: String(n) })}
        carregando={!dados && !erro} aviso={!dados && erro ? <ErroDaApi erro={erro} /> : undefined} />
      {dados && <Agora indicadores={numerosDeAgora(dados.atual)} />}
    </>
  );

  const nomeDoResponsavel = responsavel
    ? responsaveis.find((p) => p.id === responsavel)?.nome ?? dados?.atual.responsavel?.nome ?? null : null;

  return (
    <PainelDoEspaco titulo="Painel do Comercial"
      hoje={<HojeDoComercial responsavel={responsavel} nomeDoResponsavel={nomeDoResponsavel} />} andamento={andamento}
      atualizadoEm={atualizadoEm} abaixo={dados ? <Detalhes d={dados.atual} /> : null} />
  );
}
